import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { BrandLockup } from "@/components/LogoMark";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== confirm) return setError("The two passwords do not match.");
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/password/reset", { token, password: pwd });
      toast.success("Password reset. Please sign in.");
      navigate("/login");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-sm" data-testid="reset-password-page">
        <div className="mb-10"><BrandLockup /></div>
        <p className="label-caps">Account recovery</p>
        <h1 className="font-display text-3xl mt-2">Set a new password</h1>
        {!token ? (
          <p className="mt-6 text-sm text-red-800 dark:text-red-400" data-testid="reset-missing-token">
            This link is incomplete. Please request a fresh reset link from the sign-in page.
          </p>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-password-form">
            <div className="space-y-2">
              <Label className="label-caps">New password</Label>
              <div className="relative">
                <Input data-testid="reset-password-input" type={show ? "text" : "password"} required
                  minLength={6} value={pwd} onChange={(e) => setPwd(e.target.value)}
                  className="rounded-none pr-10 focus:ring-2 focus:ring-blue-800" />
                <button type="button" data-testid="reset-password-toggle" onClick={() => setShow((s) => !s)}
                  aria-label={show ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-200">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Confirm password</Label>
              <Input data-testid="reset-confirm-input" type={show ? "text" : "password"} required
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="rounded-none focus:ring-2 focus:ring-blue-800" />
            </div>
            {error && <p className="text-sm text-red-800 dark:text-red-400" data-testid="reset-error">{error}</p>}
            <Button type="submit" disabled={busy} data-testid="reset-submit-button"
              className="w-full rounded-none bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs h-11 transition-colors duration-200">
              {busy ? "Saving…" : "Save new password"}
            </Button>
          </form>
        )}
        <button type="button" data-testid="reset-back-to-login" onClick={() => navigate("/login")}
          className="mt-8 text-xs uppercase tracking-widest text-blue-900 dark:text-blue-400">
          Back to sign in
        </button>
      </div>
    </div>
  );
}
